/**
 * A condition node on a run's canvas (Feature 79 D34): marked when its gate
 * took it, faint and not taken when it took another or was skipped, as its
 * links and steps are.
 */
import { Handle, Position, type Node, type NodeProps } from '../../ui/flow.js';
import { CONDITION_WIDTH, conditionHead } from './ConditionNode.js';
import type { RunConditionFlowNode } from './run-flow.js';

export type RunConditionNodeData = RunConditionFlowNode['data'] & Record<string, unknown>;
export type RunConditionNodeType = Node<RunConditionNodeData, 'runCondition'>;

const ANCHOR = { opacity: 0, width: 1, height: 1, minWidth: 0, minHeight: 0, border: 'none' };

/** Its line on a run: taken, not taken, or what it tests while its gate waits. */
export function runConditionWords(data: Pick<RunConditionNodeData, 'state' | 'words'>): string {
  if (data.state === 'taken') return 'taken';
  if (data.state === 'not_taken') return 'not taken';
  return data.words;
}

export function RunConditionNode({ data }: NodeProps<RunConditionNodeType>) {
  const taken = data.state === 'taken';
  const faint = data.state === 'not_taken' || data.dimmed;
  return (
    <div
      style={{
        width: CONDITION_WIDTH,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: '5px 9px',
        borderRadius: 'var(--radius-control)',
        background: taken ? 'var(--ok-tint)' : 'var(--blue-tint)',
        border: taken ? '1.5px solid var(--ok)' : '1px solid var(--border-input)',
        opacity: faint ? 0.45 : 1,
      }}
    >
      <Handle type="target" position={Position.Left} isConnectable={false} style={ANCHOR} />
      <span
        className="mono"
        style={{
          fontSize: 'var(--text-meta)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: taken ? 'var(--ok)' : 'var(--blue)',
        }}
      >
        {conditionHead(data)}
      </span>
      {data.number !== null && (
        <span
          className="mono"
          style={{ fontSize: 'var(--text-body-md)', fontWeight: 'var(--weight-semibold)' }}
        >
          {data.branch}
        </span>
      )}
      <span
        style={{
          fontSize: 'var(--text-meta)',
          fontWeight: taken ? 'var(--weight-semibold)' : 'var(--weight-regular)',
          color: taken ? 'var(--ok)' : 'var(--text-soft)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {runConditionWords(data)}
      </span>
      <Handle type="source" position={Position.Right} isConnectable={false} style={ANCHOR} />
    </div>
  );
}
